import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CellContent,
  ResourceCategoryEnum,
  TableDataModel,
} from '@protos/content_management/content.db_pb';
import { getMediaBasePath } from '../../../../commonUtils/images';
import { pxToRem } from '../../../../commonUtils/resizeUtils';
import { IStyles } from '../../../../commonUtils/styleUtils';
import ImageWrapper from '../../../elements/ImageWrapper';
import { GeneoKatex } from '../../Question/GeneoKatex';

const styles: IStyles = {
  table: {
    borderCollapse: 'separate',
    borderSpacing: '0 10px',
  },
  tableBody: {
    '& tr': {
      boxShadow: '0px 0px 10px #E7E7E7D9',
    },
    '& tr:first-child': {
      borderTopLeftRadius: '15px',
      borderTopRightRadius: '15px',
      '& td:first-child': {
        borderTopLeftRadius: '15px',
      },
      '& td:last-child': {
        borderTopRightRadius: '15px',
      },
    },
    '& tr:last-child': {
      borderBottomLeftRadius: '15px',
      borderBottomRightRadius: '15px',
      '& td:first-child': {
        borderBottomLeftRadius: '15px',
      },
      '& td:last-child': {
        borderBottomRightRadius: '15px',
      },
    },
  },
  tableCell: {
    borderBottom: 'unset',
    backgroundColor: 'common.white',
    '&:not(:last-child)': {
      borderRight: '1px solid #B5B5B5',
    },
    fontFamily: 'Lato',
    color: '#1D1D1D',
    fontWeight: '400',
    fontSize: {
      xs: pxToRem(16),
      md: pxToRem(24),
    },
  },
  tableCellHeader: {
    borderBottom: 'unset',
    backgroundColor: '#DAEEF6',
    '&:not(:last-child)': {
      borderRight: '1px solid #079BD5',
    },
    fontFamily: 'Lato',
    color: '#1D1D1D',
    fontWeight: '800',
    fontSize: {
      xs: pxToRem(22),
      md: pxToRem(30),
    },
    textAlign: 'center',
  },
  tableCellFooter: {
    borderBottom: 'unset',
    backgroundColor: '#DAEEF6',
    '&:not(:last-child)': {
      borderRight: '1px solid #079BD5',
    },
    fontFamily: 'Lato',
    color: '#1D1D1D',
    fontWeight: '400',
    fontSize: {
      xs: pxToRem(16),
      md: pxToRem(24),
    },
  },
};

interface IProps {
  table: TableDataModel;
  resourceCategory?: ResourceCategoryEnum;
}

const getArrayFromTableContent = (content: CellContent[]) => {
  const array: CellContent[][] = [];
  content.forEach((con) => {
    // console.log(con);
    if (!Array.isArray(array[con.row])) {
      array[con.row] = [];
    }
    array[con.row][con.column] = con;
  });
  return array;
};

const createArrayFromStartAndEndIndex = (
  startIndex: number,
  endIndex: number
) => {
  const arr = [];
  for (let i = startIndex; i <= endIndex; i++) {
    arr.push(i);
  }
  return arr;
};

export const TableElement = (props: IProps) => {
  const { table, resourceCategory } = props;
  const { content, title, hasHeader, hasFooter, mergedCells } = table;
  const tableArray = getArrayFromTableContent(content);
  let minRow = Number.MAX_SAFE_INTEGER;
  let minCol = Number.MAX_SAFE_INTEGER;
  let maxRow = 0;
  let maxCol = 0;
  tableArray.forEach((tRow) => {
    tRow.forEach((tCell) => {
      const { row, column } = tCell;
      if (row < minRow) {
        minRow = row;
      }
      if (row > maxRow) {
        maxRow = row;
      }
      if (column < minCol) {
        minCol = column;
      }
      if (column > maxCol) {
        maxCol = column;
      }
    });
  });
  const rowStartIndex = 0;
  const rowEndIndex: number = tableArray.length - 1;
  const bodyRowIndexes: number[] = createArrayFromStartAndEndIndex(
    rowStartIndex,
    rowEndIndex
  );

  const isMergeCellStart = (rowIndex: number, colIndex: number) => {
    return mergedCells.some((mCell) => {
      const rowStart = mCell.start?.row;
      const colStart = mCell.start?.column;
      if (typeof rowStart === 'number' && typeof colStart === 'number') {
        return rowStart === rowIndex && colStart === colIndex;
      }
      return false;
    });
  };

  const getTableDimension = (
    rowIndex: number,
    colIndex: number
  ): { rowSpan: number; colSpan: number } => {
    const mIndex = mergedCells.findIndex((mCell) => {
      const rowStart = mCell.start?.row;
      const rowEnd = mCell.end?.row;
      const columnStart = mCell.start?.column;
      const columnEnd = mCell.end?.column;

      if (
        typeof rowStart === 'number' &&
        typeof rowEnd === 'number' &&
        typeof columnStart === 'number' &&
        typeof columnEnd === 'number'
      ) {
        return (
          rowIndex >= rowStart &&
          rowIndex <= rowEnd &&
          colIndex >= columnStart &&
          colIndex <= columnEnd
        );
      }
      return false;
    });

    if (mIndex === -1) {
      return { colSpan: 1, rowSpan: 1 };
    }

    const mCell = mergedCells[mIndex];
    if (isMergeCellStart(rowIndex, colIndex)) {
      return {
        rowSpan: 1 + (mCell.end?.row || 0) - (mCell.start?.row || 0),
        colSpan: 1 + (mCell.end?.column || 0) - (mCell.start?.column || 0),
      };
    }

    return { rowSpan: 1, colSpan: 1 };
  };

  const getTableCellFromValue = (val: CellContent) => {
    const isHeaderRow = hasHeader && val.row === 0;
    const isFooterRow = hasFooter && val.row === bodyRowIndexes.length - 1;
    const cellStyle = isHeaderRow
      ? styles.tableCellHeader
      : isFooterRow
      ? styles.tableCellFooter
      : styles.tableCell;
    if (val.textType) {
      return (
        <TableCell {...getTableDimension(val.row, val.column)} sx={cellStyle}>
          <span
            dangerouslySetInnerHTML={{
              __html: new GeneoKatex(val.textType.text).parsedHtml(),
            }}
          ></span>
        </TableCell>
      );
    }
    if (val.imageType) {
      return (
        <TableCell {...getTableDimension(val.row, val.column)} sx={cellStyle}>
          <ImageWrapper
            name={val.imageType.imageName}
            styles={{ width: '100%', minHeight: '40px' }}
            path={getMediaBasePath(
              val.imageType.imageUrl,
              resourceCategory ===
                ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                ? 'userDataBucket'
                : 'processedMediaBucket'
            )}
          />
        </TableCell>
      );
    }
    return (
      <TableCell {...getTableDimension(val.row, val.column)} sx={cellStyle} />
    );
  };

  return (
    <>
      {title && <Typography variant="elementH1">{title}</Typography>}
      <Table sx={styles.table}>
        <TableBody sx={styles.tableBody}>
          {createRangeArray(minRow, maxRow).map((rowIndex) => {
            return (
              <TableRow sx={styles.tableRow} key={rowIndex}>
                {createRangeArray(minCol, maxCol).map((colIndex) => {
                  const tableCell = tableArray[rowIndex][colIndex];
                  if (tableCell) {
                    return getTableCellFromValue(tableCell);
                  }
                  return getTableCellFromValue(
                    new CellContent({
                      row: rowIndex,
                      column: colIndex,
                    })
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};

function createRangeArray(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}
